import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, InlineButton } from '@skatteetaten/ds-buttons';
import { dsI18n } from '@skatteetaten/ds-core-utils';
import { ChevronLeftSVGpath, ChevronRightSVGpath } from '@skatteetaten/ds-icons';
import './styles.css';


const canHaveElipsisEnd = (active, sibling, lastPage)=>active + sibling + 1 < lastPage - 1;
const canHaveElipsisStart = (active, sibling)=>active - sibling - 1 > 2;
const getRange = (start, end)=>Array.from({
        length: end - start + 1
    }, (_, i)=>start + i);
const isElipsis = (paging)=>{
    return paging?.toString().match(/^elips/) ? true : false;
};

var styles = {
    "button": "PaginationList-module_button__kibml",
    "elipsis": "PaginationList-module_elipsis__xxPTO",
    "element_leftArrow": "PaginationList-module_element_leftArrow__vdaNF",
    "element_rightArrow": "PaginationList-module_element_rightArrow__C0MXq"
};

const FirstLastPageButton = ({ activePage, navigateDirection, hidePrevNextButtonTitle, t, handleChange })=>{
    const style = navigateDirection === 'previous' ? styles.element_leftArrow : styles.element_rightArrow;
    const buttonTitle = navigateDirection === 'previous' ? t('pagination.PreviousButtonTitle') : t('pagination.NextButtonTitle');
    const icon = navigateDirection === 'previous' ? ChevronLeftSVGpath : ChevronRightSVGpath;
    const multiplication = navigateDirection === 'next' ? 1 : -1;
    return /*#__PURE__*/ jsx("li", {
        className: style,
        children: hidePrevNextButtonTitle ? /*#__PURE__*/ jsx(IconButton, {
            type: 'button',
            svgPath: icon,
            title: buttonTitle,
            onClick: ()=>handleChange(activePage + multiplication)
        }) : /*#__PURE__*/ jsx(InlineButton, {
            iconPosition: navigateDirection === 'previous' ? 'left' : 'right',
            svgPath: icon,
            className: styles.button,
            onClick: ()=>handleChange(activePage + multiplication),
            children: buttonTitle
        })
    });
};
const getFirstOrLastRef = ({ paging, lastPage, firstPageRef, lastPageRef })=>{
    if (paging === 1) return firstPageRef;
    else if (paging === lastPage) return lastPageRef;
    return undefined;
};
const PaginationList = ({ lastPage, currentPage, sibling, handleChange, firstPageRef, lastPageRef, hidePrevNextButtonTitle })=>{
    const { t } = useTranslation('ds_navigation', {
        i18n: dsI18n
    });
    const barList = [];
    let rangeStart = 2;
    let rangeEnd = lastPage - 1;
    barList.push(1);
    if (canHaveElipsisStart(currentPage, sibling)) {
        if (currentPage > 2) {
            rangeStart = currentPage - sibling;
        }
        barList.push('elipsbeforeactive');
    }
    if (rangeStart < currentPage + 1) {
        barList.push(...getRange(rangeStart, currentPage - 1));
    }
    if (currentPage > 1 && currentPage < lastPage) {
        barList.push(currentPage);
    }
    if (canHaveElipsisEnd(currentPage, sibling, lastPage)) {
        rangeEnd = currentPage + sibling;
    }
    if (rangeEnd > currentPage) {
        barList.push(...getRange(currentPage + 1, rangeEnd));
    }
    if (canHaveElipsisEnd(currentPage, sibling, lastPage)) {
        barList.push('elipsafteractive');
    }
    barList.push(lastPage);
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            currentPage > 1 && /*#__PURE__*/ jsx(FirstLastPageButton, {
                activePage: currentPage,
                navigateDirection: 'previous',
                handleChange: handleChange,
                t: t,
                hidePrevNextButtonTitle: hidePrevNextButtonTitle
            }),
            barList.map((paging)=>/*#__PURE__*/ jsx("li", {
                    children: isElipsis(paging) ? /*#__PURE__*/ jsx("p", {
                        className: styles.elipsis,
                        "aria-label": dsI18n.t('pagination.EllipsisAltText'),
                        children: '...'
                    }) : /*#__PURE__*/ jsx(Button, {
                        ref: getFirstOrLastRef({
                            paging,
                            lastPage,
                            firstPageRef,
                            lastPageRef
                        }),
                        variant: paging === currentPage ? 'primary' : 'tertiary',
                        className: styles.button,
                        ariaCurrent: paging === currentPage ? true : undefined,
                        onClick: ()=>handleChange(Number(paging)),
                        children: paging.toString()
                    })
                }, paging)),
            currentPage < lastPage && /*#__PURE__*/ jsx(FirstLastPageButton, {
                activePage: currentPage,
                navigateDirection: 'next',
                handleChange: handleChange,
                t: t,
                hidePrevNextButtonTitle: hidePrevNextButtonTitle
            })
        ]
    });
};
PaginationList.displayName = 'PaginationList';

export { PaginationList };
